import React, { useContext, createContext, useState, useEffect, useMemo } from 'react'
import { useShopifyCookies } from '@shopify/hydrogen-react'
import { useStoresQuery } from "~/hooks/useStoresQuery"
import Geoloc from '~/utils/geoloc'
import UseWardrobe from '~/utils/use-wardrobe'
import themeInit from '~/initializers/theme-init'

const ThemeContext = createContext()

const checkGender = () => {
  const obj = {pgGender: 'men', srGender: null}
  if (typeof localStorage === "undefined") return obj
  
  const params = new URLSearchParams(window.location.search)
  const local = localStorage.getItem('g_hash')
  let is_m = window.location.pathname.includes('/collections/mens') || params.get('g') === 'men'
  const is_w = window.location.pathname.includes('/collections/women') || params.get('g') === 'women'

  if (local && !(is_m || is_w)) return JSON.parse(local)
  if (is_m || is_w) {
    obj.pgGender = is_w ? 'women' : 'men'
    obj.srGender = is_m ? 'men' : is_w ? 'women' : null
  }
  
  return obj
}

const ThemeProvider = ({ children }) => {
  const { getDistances } = Geoloc()
  const { checkUserFavs, getOrderHist } = UseWardrobe()
  const stores_json = useStoresQuery()
  useShopifyCookies({hasUserConsent: true})
  const shiplog = (typeof localStorage !== "undefined") && localStorage.getItem('shiplog')

  const [didLoad, setDidLoad] = useState(false)
  const [pageLoad, setPageLoad] = useState(false)
  const [isSHM, setIsSHM] = useState(null)
  const [isMobile, setIsMobile] = useState(null)
  const [isTablet, setIsTablet] = useState(null)
  const [isTouches, setIsTouches] = useState(null)
  const [locate, setLocate] = useState(null)
  const [freeze, setFreeze] = useState(false)
  const [gender, setGender] = useState(() => checkGender())
  const [analytics, setAnalytics] = useState({})
  const [pageProps, setPageProps] = useState({pageType: null})
  const [transBg, setTransBg] = useState('#fff')
  
  const [mobileMenu, setMobileMenu] = useState(false)
  const [blockHeader, setBlockHeader] = useState(false)
  const [search, setSearch] = useState(false)
  const [storesData, setStoresData] = useState(stores_json)
  const [storesOpen, setStoresOpen] = useState(false)
  const [showLoginWarn, setShowLoginWarn] = useState(false)
  
  const [collPanel, setCollPanel] = useState(false)
  const [collGender, setCollGender] = useState(null)

  const [bread, setBread] = useState(false)
  const [plineInv, setPlineInv] = useState(false)
  const [pdpVariant, setPdpVariant] = useState(false)
  const [showSizing, setShowSizing] = useState(false)
  const [shipPref, setShipPref] = useState('ship')
  const [quick, setQuick] = useState(false)

  const [customer, setCustomer] = useState(false)
  const [checkedFavs, setCheckedFavs] = useState(false)
  const [custfavs, setCustfavs] = useState(false)
  const [orders, setOrders] = useState([])
  const [order, setOrder] = useState(null)

  const storesSort = () => {
    let coords = false
    if (!locate.usrCoords && !!locate.pdpStore) {
      const pdpstore = storesData.flatMap(x => x.stores).find(x => x.short_name === locate.pdpStore)
      if (pdpstore) coords = pdpstore.coordinates
    }
    let sorted = getDistances(storesData, locate, coords)
    sorted = sorted.filter(x => x.stores.length > 0)
    setStoresData(sorted)

    if (locate.currStores.length > 0) {
      const currStore = storesData.flatMap(x => x.stores)
                    .filter(x => !!x.pickup_enabled)
                    .find(x => x.short_name === locate.currStores[0])
      if (currStore) setShipPref('pickup')
    }
  }

  const checkFavs = async (fav_id=false) => {
    if (!localStorage.getItem('ward')) return
    if (!customer) return
    if (!!custfavs) return

    let favs = false
    if (fav_id) {
      favs = await checkUserFavs({id: fav_id})
      
    } else if (customer) {
      favs = await checkUserFavs({sid: customer.id})
    }

    if (!favs) {
      setCustfavs(null)
      return
    }

    setCustfavs(favs)
    if (!localStorage.getItem('wrdrb')) localStorage.setItem('wrdrb', favs.id)
  }

  useEffect(() => {
    if (!didLoad) {
      setDidLoad(true)
      themeInit({
        shiplog, setGender, setIsMobile, setIsTablet, setIsTouches,
        setLocate, setCustomer, setIsSHM
      })
    }

    if (!pageLoad && document.readyState === 'complete') {
      setPageLoad(true)
    } else if (!pageLoad) {
      const loadDone = () => {
        setPageLoad(true)
        window.removeEventListener('load', loadDone)
      }
      window.addEventListener('load', loadDone)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!didLoad) return
    if (!locate) return
    localStorage.setItem('bmloc', JSON.stringify(locate))
    storesSort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locate])

  useEffect(() => {
    document.querySelector('html').style.overflow = freeze ? 'hidden' : ''
  }, [freeze])

  useEffect(() => {
    if (!didLoad) return
    let loggedout = !customer

    if (loggedout) {
      setCustfavs(null)
      return
    }
    const ward_id = localStorage.getItem('wrdrb')

    if (!checkedFavs && ward_id) {
      setCheckedFavs(true)
      checkFavs(ward_id)
      return
    }

    const skip = localStorage.getItem('wrdrb') && !loggedout && !custfavs
    if (!loggedout && !skip) checkFavs()
    loggedout = !customer || !customer?.full
      
    if (!loggedout && !localStorage.getItem('ords') && localStorage.getItem('ward')) getOrderHist(customer)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer])

  useEffect(() => {
    if (!didLoad) return
    localStorage.setItem('g_hash', JSON.stringify(gender))
  }, [gender])

  const updateGender = (gend) => {
    let new_gend = gend
    if (gend === 'm' || gend === 'w') {
      new_gend = gend === 'm' ? 'men' : 'women'
    }
    if (new_gend !== 'men' && new_gend !== 'women') return

    setGender({pgGender: new_gend, srGender: new_gend})
  }

  const providerValue = useMemo(() => ({
    pageLoad, 
    isSHM, setIsSHM, shiplog, 
    analytics, setAnalytics,
    isMobile, isTablet, isTouches,
    setIsMobile, setIsTablet, setIsTouches,
    locate, setLocate, 
    gender, setGender: updateGender,
    bread, setBread,
    freeze, setFreeze,
    pdpVariant, setPdpVariant,
    mobileMenu, setMobileMenu,
    collPanel, setCollPanel,
    collGender, setCollGender,
    storesOpen, setStoresOpen,
    blockHeader, setBlockHeader,
    plineInv, setPlineInv,
    quick, setQuick,
    search, setSearch,
    showSizing, setShowSizing,
    customer, setCustomer,
    custfavs, setCustfavs,
    orders, setOrders,
    order, setOrder,
    pageProps, setPageProps,
    transBg, setTransBg,
    shipPref, setShipPref,
    storesData, setStoresData,
    showLoginWarn, setShowLoginWarn
  }), [
    pageLoad, isSHM, shiplog, analytics, isMobile, isTablet, isTouches,
    locate, gender, bread, freeze, pdpVariant, mobileMenu,
    collPanel, collGender, storesOpen, blockHeader, plineInv, quick, search,
    showSizing, customer, custfavs, orders, order, pageProps, transBg,
    shipPref, storesData, showLoginWarn
  ])

  return (
    <ThemeContext.Provider value={providerValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme() {
  return useContext(ThemeContext)
}

export { ThemeContext, ThemeProvider }


// const defaultState = {
//   dark: false,
//   toggleDark: () => {},
// }

// const supportsDarkMode = () =>
//   window.matchMedia("(prefers-color-scheme: dark)").matches === true

// const state = {
//   dark: false,
//   panel: 'panel-men',
//   sub: false
// }

// const toggleDark = () => {
//   let dark = !this.state.dark
//   localStorage.setItem("dark", JSON.stringify(dark))
//   this.setState({ dark })
// }
// state,
// toggleDark: toggleDark,
// togglePanel: togglePanel,
// toggleSub: toggleSub,

// useEffect(() => {
//   const lsDark = JSON.parse(localStorage.getItem("dark"))
//   if (lsDark) {
//     this.setState({ dark: lsDark })
//   } else if (supportsDarkMode()) {
//     this.setState({ dark: true })
//   }
// }, []);
